import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';

/**
 * A sync-log failure is retried by enqueueing a job. A deposit forfeiture entry
 * is retried in place, so its response says what happened instead: posted,
 * refused again, or set aside because the deposit is not in QuickBooks.
 */
export interface RetryResponse {
  enqueued: boolean;
  jobId: string | null;
  outcome?: 'posted' | 'skipped' | 'setAside' | 'failed';
  error?: string;
}

export function useAccountingSyncRetry() {
  const queryClient = useQueryClient();
  return useApiMutation<RetryResponse, string>({
    mutationFn: (logId) =>
      apiPost<RetryResponse>(operationsClient, settingsEndpoints.accountingSyncRetry(logId)),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.accountingFailures() });
      void queryClient.invalidateQueries({ queryKey: settingsKeys.accountingDashboard() });
      void queryClient.invalidateQueries({ queryKey: settingsKeys.accountingQueueDepth() });
      announceRetry(data);
    },
  });
}

export function announceRetry(data: RetryResponse): void {
  switch (data.outcome) {
    case 'posted':
      toast.success('Posted to QuickBooks');
      return;
    case 'failed':
      toast.error(`QuickBooks refused it again: ${data.error ?? 'unknown error'}`);
      return;
    case 'setAside':
      toast.info('Set aside: the deposit is not in QuickBooks yet. It will post once the deposit syncs.');
      return;
    case 'skipped':
      toast.info('Already being posted — check back in a minute');
      return;
    default:
      toast.success(data.enqueued ? 'Retry enqueued' : 'Retry skipped — already queued');
  }
}
